/* ============================================================
   Laley PDP Clean v4 — core
   Modulær PDP-runtime. Den gamle v3-monolitten er kun historisk arkiv.
   Definerer utils, LaleyPDP-kjerneklassen og modulregisteret.
   Lastekontrakt (defer, i denne rekkefølgen):
   core → variants/siblings/reviews/commerce/bundle/gallery/sticky/product-ui → boot
   ============================================================ */
(() => {
  if (window.LaleyPDPCleanV4) return;
  // Hindre dobbel-init dersom historisk v3-monolitt skulle ligge cached et sted.
  window.__LaleyPdpCleanV31Loaded = true;

  const SECTION_SELECTOR = '[data-pdp-section]';
  const instances = new WeakMap();

  const cssEscape = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(String(value));
    return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  };

  const escapeHTML = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const normalizeSearchText = (value) => String(value || '')
    .toLowerCase()
    .replace(/å/g, 'a')
    .replace(/ø/g, 'o')
    .replace(/æ/g, 'ae')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const readJSON = (el, fallback) => {
    if (!el) return fallback;
    try {
      return JSON.parse(el.textContent || '');
    } catch (error) {
      console.warn('[PDP] Invalid JSON', error);
      return fallback;
    }
  };

  const scrollLocks = new Set();
  let previousRootOverflow = null;
  const lockRootScroll = (token = Symbol('pdp-scroll-lock')) => {
    if (!scrollLocks.size) {
      previousRootOverflow = document.documentElement.style.overflow || '';
      document.documentElement.style.overflow = 'hidden';
    }
    scrollLocks.add(token);
    return token;
  };
  const unlockRootScroll = (token) => {
    if (!token) return;
    scrollLocks.delete(token);
    if (scrollLocks.size) return;
    document.documentElement.style.overflow = previousRootOverflow || '';
    previousRootOverflow = null;
  };

  const PDP_BOOT_SEQUENCE = Object.freeze([
    { name: 'variants', methods: ['bindOptions'] },
    { name: 'commerce', methods: ['bindForm', 'bindOutfitAdds', 'bindTabs', 'bindPurchaseOptions'] },
    { name: 'outfitBadges', methods: ['bindOutfitBadgeTypography'] },
    { name: 'gallery', methods: ['bindGallery'] },
    { name: 'productInfo', methods: ['bindReviewsJump', 'bindSizeGuide', 'bindModelInfoOverlay', 'bindDetailsClamp', 'bindLagetForDeg'] },
    { name: 'reviews', methods: ['bindReviewLabelLocale', 'bindReviewWidgetPolish', 'bindAllReviewsHeader', 'bindKundeneSier'] },
    { name: 'sticky', methods: ['bindSticky'] },
    { name: 'inventory', methods: ['bindBisObserver'] },
    { name: 'bundle', methods: ['bindBundleAutoDiscover', 'bindBundleSizeSync', 'bindBundleMaterials'] },
    { name: 'galleryEnhancements', methods: ['bindGalleryZoom'] },
    { name: 'siblings', methods: ['bindSiblingColorPicker'] },
  ]);

  class LaleyPDP {
    constructor(section) {
      this.section = section;
      this.variants = readJSON(section.querySelector('[data-pdp-variants]'), []);
      this.settings = readJSON(section.querySelector('[data-pdp-settings]'), {});
      this.unsubs = [];
      this.timers = [];
      this.raf = null;
      this.stickyRaf = null;
      this.mobileStickyHeight = 58;
      this.requiresSizeSelection = section.dataset.requiresSizeSelection === 'true';
      this.currentVariant = this.variantFromUrl() || this.variantFromInput() || (this.requiresSizeSelection ? null : (this.variants[0] || null));
    }

    init() {
      this.cache();
      this.bootModules(PDP_BOOT_SEQUENCE);
      const urlParams = new URLSearchParams(window.location.search);
      const hasVariantParam = urlParams.has('variant');
      // NB: ?variant i URL er IKKE brukervalg. updateUrl skriver ?variant ved
      // hvert størrelses-tap, så reload/delte lenker/tilbake-navigasjon arvet
      // et «valg» kunden aldri tok — sticky sa «Kjøp» og la feil størrelse i
      // kurven (brukerrapportert juli-26). Størrelse må velges i økten;
      // URL-varianten beholdes kun som hint for galleri-sync under.

      // Hvis URL-variant er utsolgt men andre varianter HAR stock,
      // switch til første tilgjengelige slik at ATC blir clickable (gjelder
      // kun produkter uten påkrevd størrelsesvalg). Produkter med størrelser
      // starter ALLTID uten valgt størrelse.
      const startVariant = (this.currentVariant && !this.requiresSizeSelection)
        ? (this.currentVariant.available ? this.currentVariant : (this.variants.find(v => v.available) || this.currentVariant))
        : this.currentVariant;

      if (startVariant && startVariant !== this.currentVariant) {
        this.syncOptionInputsToVariant(startVariant);
        this.currentVariant = startVariant;
      }

      if (this.requiresSizeSelection) {
        this.currentVariant = null;
        if (this.variantInput) this.variantInput.value = '';
      }

      // CP/LSF-stil: ingen size pre-valgt. Force-uncheck alle ikke-color radioer
      // ved init.
      if (!this.userPicked) {
        this.optionGroups.forEach((group) => {
          const legend = (group.querySelector('legend')?.textContent || '').toLowerCase();
          const isColor = /farge|color|colour/.test(legend);
          if (isColor) return;
          group.querySelectorAll('[data-pdp-option-input]').forEach((input) => {
            input.checked = false;
          });
        });
      }

      this.renderVariant(this.currentVariant, {
        updateUrl: false,
        syncGallery: Boolean(this.currentVariant),
        behavior: 'auto',
      });
      // Delelink-hint: vis riktig bilde for ?variant uten å late som at
      // størrelsen er valgt (kjøpsmodus forblir 'pick').
      if (hasVariantParam && this.requiresSizeSelection) {
        const urlHint = this.variantFromUrl();
        if (urlHint) this.syncGalleryToVariant(urlHint, 'auto');
      }
      this.updateOptionStates();
      // Hvis userPicked (delelink), fyll dropdown-triggere med valgte verdier
      if (this.userPicked) {
        this.section.querySelectorAll('[data-pdp-dropdown]').forEach((dropdown) => {
          const input = dropdown.querySelector('[data-pdp-option-input]:checked');
          const trigger = dropdown.querySelector('[data-pdp-dropdown-value]');
          if (input && trigger) trigger.textContent = input.value;
        });
      }
      // Klaviyo Reviews-widget hydrerer noen ganger først ved variant-change.
      // Dispatch en sekundær variant:change-event etter init slik at widgeten
      // får signal om at produkt-konteksten er klar — gir konsistent visning
      // av stjerner + count uten at brukeren må klikke en størrelse først.
      this.nudgeKlaviyoHydration();
    }

    bootModules(sequence) {
      sequence.forEach((module) => {
        module.methods.forEach((method) => {
          if (typeof this[method] !== 'function') {
            console.warn(`[PDP] Missing ${module.name} module method: ${method}`);
            return;
          }
          this[method]();
        });
      });
    }

    destroy() {
      this.unsubs.forEach((off) => off());
      this.timers.forEach((timer) => { window.clearInterval(timer); window.clearTimeout(timer); });
      if (this.raf) window.cancelAnimationFrame(this.raf);
      if (this.stickyRaf) window.cancelAnimationFrame(this.stickyRaf);
      this.section.classList.remove('pdp-clean--sticky-visible', 'pdp-clean--gallery-visible');
      instances.delete(this.section);
    }

    cache() {
      this.form = this.section.querySelector('[data-pdp-form]');
      this.variantInput = this.section.querySelector('[data-pdp-variant-id]');
      this.optionInputs = Array.from(this.section.querySelectorAll('[data-pdp-option-input]'));
      this.optionGroups = Array.from(this.section.querySelectorAll('[data-pdp-option-group]'));
      this.atc = this.section.querySelector('[data-pdp-atc]');
      this.error = this.section.querySelector('[data-pdp-error]');
      this.priceCurrent = this.section.querySelector('[data-pdp-price-current]');
      this.priceCompare = this.section.querySelector('[data-pdp-price-compare]');
      this.priceSaving = this.section.querySelector('[data-pdp-price-saving]');
      this.bis = this.section.querySelector('[data-pdp-bis]');
      this.bundle = this.section.querySelector('[data-pdp-bundle]');
      this.bundlePiecePrice = this.section.querySelector('[data-pdp-bundle-piece-price]');
      this.galleryWrap = this.section.querySelector('[data-pdp-gallery-wrap]');
      this.gallery = this.section.querySelector('[data-pdp-gallery]');
      this.galleryProgressBar = this.section.querySelector('[data-pdp-gallery-progress-bar]');
      this.sticky = this.section.querySelector('[data-pdp-sticky-bar]');
      this.stickyButton = this.section.querySelector('[data-pdp-sticky-atc]');
      this.stickyPrice = this.section.querySelector('[data-pdp-sticky-price]');
      this.stickyImg = this.section.querySelector('[data-pdp-sticky-img]');
      this.refreshStickyMetrics();
    }

    on(target, event, handler, options) {
      if (!target) return;
      target.addEventListener(event, handler, options);
      this.unsubs.push(() => target.removeEventListener(event, handler, options));
    }

    money(cents) {
      if (typeof cents !== 'number') return '';
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && this.settings.moneyFormat) {
        return window.Shopify.formatMoney(cents, this.settings.moneyFormat);
      }
      return `${(cents / 100).toLocaleString('nb-NO', { maximumFractionDigits: 0 })} kr`;
    }

    cartUrl(path) {
      const root = window.Shopify?.routes?.root || '/';
      return `${root}${String(path).replace(/^\//, '')}`;
    }

    async fetchProductJson(handle) {
      const normalizedHandle = String(handle || '').trim().replace(/^\/+|\/+$/g, '');
      if (!normalizedHandle) return null;
      if (!this.productJsonCache) this.productJsonCache = new Map();
      if (this.productJsonCache.has(normalizedHandle)) return this.productJsonCache.get(normalizedHandle);

      let product = null;
      try {
        const response = await fetch(this.cartUrl(`products/${normalizedHandle}.js`), { headers: { Accept: 'application/json' } });
        if (response.ok) product = await response.json();
      } catch (e) {
        product = null;
      }

      this.productJsonCache.set(normalizedHandle, product);
      return product;
    }
  }

  window.LaleyPDPCleanV4 = {
    SECTION_SELECTOR,
    instances,
    LaleyPDP,
    BOOT_SEQUENCE: PDP_BOOT_SEQUENCE,
    utils: { cssEscape, escapeHTML, normalizeSearchText, readJSON, lockRootScroll, unlockRootScroll },
    hooks: {},
    modules: [],
    registerModule(name, methods) {
      Object.assign(LaleyPDP.prototype, methods);
      this.modules.push(name);
    },
  };
})();
